"use client";
/*
 * Documentation:
 * Accordion — https://app.subframe.com/4f62853ee923/library?component=Accordion_d2e81e20-863a-4027-826a-991d8910efd9
 * Badge — https://app.subframe.com/4f62853ee923/library?component=Badge_97bdb082-1124-4dd7-a335-b14b822d0157
 * DC_MetaIntro_ActionStack — https://app.subframe.com/4f62853ee923/library?component=DC_MetaIntro_ActionStack_c2a1f933-7ab4-4ec2-94fe-f4cfdc04d0e0
 * DC_MetaIntro_DrawerContent — https://app.subframe.com/4f62853ee923/library?component=DC_MetaIntro_DrawerContent_3c91200b-027e-40a5-90f9-003feaf173d3
 * Link Button — https://app.subframe.com/4f62853ee923/library?component=Link+Button_a4ee726a-774c-4091-8c49-55b659356024
 */

import React from "react";
import { FeatherArrowUpRight } from "@subframe/core";
import { FeatherLayers } from "@subframe/core";
import { FeatherUser } from "@subframe/core";
import * as SubframeUtils from "../utils";
import { Accordion } from "./Accordion";
import { Badge } from "./Badge";
import { Dc_MetaIntro_ActionStack } from "./Dc_MetaIntro_ActionStack";
import { LinkButton } from "./LinkButton";

interface BadgeStackProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  stack?: React.ReactNode;
  className?: string;
}

const BadgeStack = React.forwardRef<HTMLDivElement, BadgeStackProps>(
  function BadgeStack(
    { children, stack, className, ...otherProps }: BadgeStackProps,
    ref
  ) {
    return stack ? (
      <div
        className={SubframeUtils.twClassNames(
          "flex w-full items-start gap-4",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        {stack}
      </div>
    ) : null;
  }
);

interface VersionLinkProps extends React.ComponentProps<typeof LinkButton> {
  children?: React.ReactNode;
  className?: string;
}

const VersionLink = React.forwardRef<
  React.ElementRef<typeof LinkButton>,
  VersionLinkProps
>(function VersionLink(
  { children, className, ...otherProps }: VersionLinkProps,
  ref
) {
  return (
    <LinkButton
      className={className}
      size="small"
      icon={<FeatherLayers />}
      ref={ref}
      {...otherProps}
    >
      {children}
    </LinkButton>
  );
});

interface TranslationLinkProps extends React.ComponentProps<typeof LinkButton> {
  children?: React.ReactNode;
  className?: string;
}

const TranslationLink = React.forwardRef<
  React.ElementRef<typeof LinkButton>,
  TranslationLinkProps
>(function TranslationLink(
  { children, className, ...otherProps }: TranslationLinkProps,
  ref
) {
  return (
    <LinkButton
      className={className}
      variant="brand"
      size="small"
      iconRight={<FeatherArrowUpRight />}
      ref={ref}
      {...otherProps}
    >
      {children}
    </LinkButton>
  );
});

interface BasicItemProps extends React.HTMLAttributes<HTMLDivElement> {
  head?: React.ReactNode;
  text?: React.ReactNode;
  linkSlot?: React.ReactNode;
  className?: string;
}

const BasicItem = React.forwardRef<HTMLDivElement, BasicItemProps>(
  function BasicItem(
    { head, text, linkSlot, className, ...otherProps }: BasicItemProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "flex w-full flex-col items-start gap-2 pb-8",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        {head ? (
          <span className="w-full text-body-bold font-body-bold text-default-font">
            {head}
          </span>
        ) : null}
        {text ? (
          <span className="text-body font-body text-default-font">{text}</span>
        ) : null}
        {linkSlot ? (
          <div className="flex w-full flex-col items-start gap-2">
            {linkSlot}
          </div>
        ) : null}
      </div>
    );
  }
);

interface BasicLinkProps extends React.ComponentProps<typeof LinkButton> {
  children?: React.ReactNode;
  className?: string;
}

const BasicLink = React.forwardRef<
  React.ElementRef<typeof LinkButton>,
  BasicLinkProps
>(function BasicLink(
  { children, className, ...otherProps }: BasicLinkProps,
  ref
) {
  return (
    <LinkButton
      className={SubframeUtils.twClassNames("text-left", className)}
      variant="brand"
      ref={ref}
      {...otherProps}
    >
      {children}
    </LinkButton>
  );
});

interface AuthorCardProps extends React.HTMLAttributes<HTMLDivElement> {
  linkSlot?: React.ReactNode;
  placeOfWork?: React.ReactNode;
  authorName?: React.ReactNode;
  className?: string;
}

const AuthorCard = React.forwardRef<HTMLDivElement, AuthorCardProps>(
  function AuthorCard(
    {
      linkSlot,
      placeOfWork,
      authorName,
      className,
      ...otherProps
    }: AuthorCardProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "flex w-full flex-col items-start gap-2 rounded-md bg-neutral-50 px-6 py-6",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        <Badge icon={<FeatherUser />}>{authorName}</Badge>
        {placeOfWork ? (
          <span className="text-caption font-caption text-subtext-color">
            {placeOfWork}
          </span>
        ) : null}
        {linkSlot ? (
          <div className="flex flex-col items-start gap-2">{linkSlot}</div>
        ) : null}
      </div>
    );
  }
);

interface AuthorCardLinkProps extends React.ComponentProps<typeof LinkButton> {
  children?: React.ReactNode;
  className?: string;
}

const AuthorCardLink = React.forwardRef<
  React.ElementRef<typeof LinkButton>,
  AuthorCardLinkProps
>(function AuthorCardLink(
  { children, className, ...otherProps }: AuthorCardLinkProps,
  ref
) {
  return (
    <LinkButton
      className={SubframeUtils.twClassNames("text-left", className)}
      variant="brand"
      size="small"
      ref={ref}
      {...otherProps}
    >
      {children}
    </LinkButton>
  );
});

interface AccordionStackProps extends React.ComponentProps<typeof Accordion> {
  head?: React.ReactNode;
  children?: React.ReactNode;
  chevron?: React.ReactNode;
  isPrint?: boolean;
  className?: string;
}

const AccordionStack = React.forwardRef<
  React.ElementRef<typeof Accordion>,
  AccordionStackProps
>(function AccordionStack(
  {
    head,
    children,
    chevron,
    isPrint = false,
    className,
    ...otherProps
  }: AccordionStackProps,
  ref
) {
  return (
    <Accordion
      className={SubframeUtils.twClassNames("group/1671e191 pb-8", className)}
      trigger={
        <div className="flex w-full items-center gap-2">
          {head ? (
            <span className="grow shrink-0 basis-0 text-body-bold font-body-bold text-default-font">
              {head}
            </span>
          ) : null}
          {chevron ? (
            <div className="flex items-center gap-2">{chevron}</div>
          ) : null}
        </div>
      }
      defaultOpen={true}
      ref={ref}
      {...otherProps}
    >
      {children ? (
        <div
          className={SubframeUtils.twClassNames(
            "flex w-full grow shrink-0 basis-0 flex-col items-start gap-6 pt-3",
            { "grid grid-cols-2 gap-4": isPrint }
          )}
        >
          {children}
        </div>
      ) : null}
    </Accordion>
  );
});

interface AccordionChildrenStackProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  className?: string;
}

const AccordionChildrenStack = React.forwardRef<
  HTMLDivElement,
  AccordionChildrenStackProps
>(function AccordionChildrenStack(
  { children, className, ...otherProps }: AccordionChildrenStackProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "flex h-full w-full flex-col items-start gap-6 pt-3",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      {children ? (
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-6">
          {children}
        </div>
      ) : null}
    </div>
  );
});

interface AccordionItemProps extends React.HTMLAttributes<HTMLDivElement> {
  linkSlot?: React.ReactNode;
  text?: React.ReactNode;
  className?: string;
}

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  function AccordionItem(
    { linkSlot, text, className, ...otherProps }: AccordionItemProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "flex h-full w-full flex-col items-start gap-2",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        {text ? (
          <span className="w-full text-body font-body text-default-font">
            {text}
          </span>
        ) : null}
        {linkSlot ? (
          <div className="flex w-full flex-col items-start gap-2">
            {linkSlot}
          </div>
        ) : null}
      </div>
    );
  }
);

interface TitleProps extends React.HTMLAttributes<HTMLSpanElement> {
  children?: React.ReactNode;
  className?: string;
}

const Title = React.forwardRef<HTMLSpanElement, TitleProps>(function Title(
  { children, className, ...otherProps }: TitleProps,
  ref
) {
  return children ? (
    <span
      className={SubframeUtils.twClassNames(
        "text-heading-1 font-heading-1 text-default-font",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      {children}
    </span>
  ) : null;
});

interface TypeBadgeProps extends React.ComponentProps<typeof Badge> {
  children?: React.ReactNode;
  className?: string;
}

const TypeBadge = React.forwardRef<
  React.ElementRef<typeof Badge>,
  TypeBadgeProps
>(function TypeBadge(
  { children, className, ...otherProps }: TypeBadgeProps,
  ref
) {
  return (
    <Badge className={className} variant="neutral" ref={ref} {...otherProps}>
      {children}
    </Badge>
  );
});

interface Dc_MetaIntro_DrawerContentRootProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  title?: React.ReactNode;
  authorCardSlot?: React.ReactNode;
  paperTypeText?: React.ReactNode;
  publishedText?: React.ReactNode;
  versionLinkSlot?: React.ReactNode;
  translationLinkSlot?: React.ReactNode;
  titleSlot?: React.ReactNode;
  closeButtonSlot?: React.ReactNode;
  badgeSlot?: React.ReactNode;
  variant?: "default";
  actionSlot?: React.ReactNode;
  shareSlot?: React.ReactNode;
  children?: React.ReactNode;
  isPrint?: boolean;
  className?: string;
}

const Dc_MetaIntro_DrawerContentRoot = React.forwardRef<
  HTMLDivElement,
  Dc_MetaIntro_DrawerContentRootProps
>(function Dc_MetaIntro_DrawerContentRoot(
  {
    title,
    authorCardSlot,
    paperTypeText,
    publishedText,
    versionLinkSlot,
    translationLinkSlot,
    titleSlot,
    closeButtonSlot,
    badgeSlot,
    variant = "default",
    actionSlot,
    shareSlot,
    children,
    isPrint = false,
    className,
    ...otherProps
  }: Dc_MetaIntro_DrawerContentRootProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "group/3c91200b flex flex-col items-start gap-6 bg-default-background px-12 py-12",
        { "px-0 py-0": isPrint },
        className
      )}
      ref={ref}
      {...otherProps}
    >
      <div className="flex w-full items-center gap-6">
        {badgeSlot ? (
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">
            {badgeSlot}
          </div>
        ) : null}
        {closeButtonSlot ? (
          <div className="flex items-start gap-6">{closeButtonSlot}</div>
        ) : null}
      </div>
      {titleSlot ? (
        <div className="flex items-start gap-6">{titleSlot}</div>
      ) : null}
      {authorCardSlot ? (
        <div
          className={SubframeUtils.twClassNames(
            "flex w-full flex-col items-start gap-6",
            { "flex-col flex-nowrap gap-6 grid grid-cols-2": isPrint }
          )}
        >
          {authorCardSlot}
        </div>
      ) : null}
      <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
      <div className="flex w-full items-center justify-between">
        <div className="flex items-start gap-2">
          {publishedText ? (
            <span className="text-caption-bold font-caption-bold text-default-font">
              {publishedText}
            </span>
          ) : null}
          {versionLinkSlot ? (
            <div className="flex items-start gap-2">{versionLinkSlot}</div>
          ) : null}
        </div>
        {translationLinkSlot ? (
          <div className="flex items-center justify-between">
            {translationLinkSlot}
          </div>
        ) : null}
      </div>
      <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
      <Dc_MetaIntro_ActionStack
        className={SubframeUtils.twClassNames({ hidden: isPrint })}
        actionSlot={
          actionSlot ? (
            <div className="flex flex-wrap items-start gap-4">{actionSlot}</div>
          ) : null
        }
        shareSlot={
          shareSlot ? (
            <div className="flex items-start justify-between">{shareSlot}</div>
          ) : null
        }
      />
      <div
        className={SubframeUtils.twClassNames(
          "flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border",
          { hidden: isPrint }
        )}
      />
      {children ? (
        <div className="flex w-full flex-col items-start">{children}</div>
      ) : null}
    </div>
  );
});

export const Dc_MetaIntro_DrawerContent = Object.assign(
  Dc_MetaIntro_DrawerContentRoot,
  {
    BadgeStack,
    VersionLink,
    TranslationLink,
    BasicItem,
    BasicLink,
    AuthorCard,
    AuthorCardLink,
    AccordionStack,
    AccordionChildrenStack,
    AccordionItem,
    Title,
    TypeBadge,
  }
);
